import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLiveChannel } from '../../hooks/useLiveChannel';
import { validateApiResponse } from '../../lib/validateApiResponse';

/**
 * Heute — die Startseite des Sales-Bereichs.
 *
 * WARUM SIE EXISTIERT
 * Der Bereich hatte sechs Reiter und keine Antwort auf die erste Frage des
 * Tages: was ist dran? Wer sie beantworten wollte, musste drei Reiter
 * durchsehen und im Kopf zusammenzählen — und genau das tut nach zwei Wochen
 * niemand mehr.
 *
 * WAS SIE ZEIGT
 * Oben, was heute Handlung verlangt: fällige Wiedervorlagen, offene
 * Erstberichte, ungelesene Nachrichten. Darunter die Monatsbilanz, die in der
 * Vertriebsstrategie als gemeinsame Steuerungsgröße festgelegt ist —
 * Ansprachen, eingegangene Unterlagen, gelieferte Erstberichte, Erstgespräche,
 * Aufträge. Sie wird aus den protokollierten Stufenwechseln gerechnet, nicht
 * geschätzt: was nicht im Trichter steht, zählt nicht.
 */

const API = '/api/partner';

type SalesStufe =
  | 'notiert' | 'angesprochen' | 'unterlagen' | 'erstbericht-offen'
  | 'geliefert' | 'erstgespraech' | 'auftrag' | 'kein-bedarf';

interface VerlaufEintrag {
  id: string;
  datum: string;
  art: string;
  text: string;
  von: string;
  createdAt: string;
}

interface SalesContact {
  id: string;
  firma: string;
  kontaktperson?: string;
  stufe: SalesStufe;
  besitzer: string;
  wiedervorlage?: string;
  letzterKontakt?: string;
  folgegeschaeft?: string;
  verlauf: VerlaufEintrag[];
  dateien: unknown[];
}

interface Meilenstein {
  id: string;
  titel: string;
  wann: string;
  ab: string;
  beschreibung?: string;
  wer: string;
  done: boolean;
  doneVon?: string;
  schritte: { id: string; text: string; done: boolean }[];
}

interface PartnerMessage {
  id: string;
  from: string;
  readAt: string | null;
}

function heute(): string {
  return new Date().toISOString().slice(0, 10);
}

function monatsBeginn(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

function monatsName(): string {
  return new Date().toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function formatDate(iso?: string): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T00:00:00Z`);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

/** Ein Stufenwechsel steht im Verlauf als "alt → neu". */
function zielStufe(eintrag: VerlaufEintrag): string | null {
  if (eintrag.art !== 'stufe') return null;
  const teile = eintrag.text.split('→');
  return teile.length === 2 ? teile[1].trim() : null;
}

export default function SalesHeutePanel({ onWechsel }: { onWechsel: (ziel: 'kunden' | 'nachrichten' | 'ziele') => void }) {
  const { user } = useAuth();
  const userId = user?.id ?? '';

  const [contacts, setContacts] = useState<SalesContact[]>([]);
  const [ungelesen, setUngelesen] = useState(0);
  const [meilensteine, setMeilensteine] = useState<Meilenstein[]>([]);
  const [msFehler, setMsFehler] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const laden = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sales`, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const list = (raw as { contacts?: unknown[] }).contacts;
      if (!Array.isArray(list)) throw new Error('API /api/partner/sales: erwartet { contacts: [] }');
      setContacts(list.map(item =>
        validateApiResponse<SalesContact>(item, '/api/partner/sales[item]', {
          id: 'string',
          firma: 'string',
          kontaktperson: { type: 'string', optional: true },
          stufe: 'string',
          besitzer: 'string',
          wiedervorlage: { type: 'string', optional: true },
          letzterKontakt: { type: 'string', optional: true },
          folgegeschaeft: { type: 'string', optional: true },
          verlauf: 'array',
          dateien: 'array',
        }),
      ));
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  const ladenNachrichten = useCallback(async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${API}/messages?userId=${encodeURIComponent(userId)}`, {
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json() as { messages?: PartnerMessage[] };
      const msgs = raw.messages ?? [];
      setUngelesen(msgs.filter(m => !m.readAt && m.from !== userId).length);
    } catch (err) {
      // Der Zaehler ist Beiwerk der Seite; ein Fehler hier darf die faelligen
      // Wiedervorlagen nicht verdecken. Sichtbar bleibt er trotzdem.
      console.error('[SalesHeute] Nachrichten-Zähler:', err);
    }
  }, [userId]);

  const ladenMeilensteine = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sales/meilensteine`, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const raw = await res.json();
      const list = (raw as { meilensteine?: unknown[] }).meilensteine;
      if (!Array.isArray(list)) throw new Error('API /api/partner/sales/meilensteine: erwartet { meilensteine: [] }');
      setMeilensteine(list.map(item =>
        validateApiResponse<Meilenstein>(item, '/api/partner/sales/meilensteine[item]', {
          id: 'string', titel: 'string', wann: 'string', ab: 'string', wer: 'string', done: 'boolean',
          schritte: 'array',
          beschreibung: { type: 'string', optional: true },
          doneVon: { type: 'string', optional: true },
        }),
      ));
      setMsFehler('');
    } catch (err) {
      setMsFehler(err instanceof Error ? err.message : String(err));
    }
  }, []);

  // Auf der Startseite wird nur abgehakt, nie zurueckgenommen — wer etwas
  // rueckgaengig machen will, tut das im Ziele-Reiter, wo der ganze Stand steht.
  const hakenSchritt = useCallback(async (msId: string, schrittId: string) => {
    setMeilensteine(prev => prev.map(m => (m.id === msId
      ? { ...m, schritte: m.schritte.map(s => (s.id === schrittId ? { ...s, done: true } : s)) }
      : m)));
    try {
      const res = await fetch(`${API}/sales/meilensteine/${msId}/schritte/${schrittId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ done: true }),
        signal: AbortSignal.timeout(15000),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await ladenMeilensteine();
    } catch (err) {
      setMsFehler(err instanceof Error ? err.message : String(err));
      ladenMeilensteine();
    }
  }, [ladenMeilensteine]);

  useEffect(() => { laden(); ladenNachrichten(); ladenMeilensteine(); }, [laden, ladenNachrichten, ladenMeilensteine]);

  useLiveChannel({
    label: 'SalesHeute',
    onResync: () => { laden(); ladenNachrichten(); ladenMeilensteine(); },
    onMessage: (msg: { type?: string }) => {
      if (typeof msg.type === 'string' && msg.type.startsWith('partner-sales')) laden();
      if (typeof msg.type === 'string' && msg.type.startsWith('partner-message')) ladenNachrichten();
      if (msg.type === 'partner-sales-meilenstein') ladenMeilensteine();
    },
  });

  const meine = useMemo(() => contacts.filter(c => c.besitzer === userId), [contacts, userId]);

  // Was schon angelaufen und noch nicht fertig ist. Der Rest steht im
  // Ziele-Reiter — die Startseite beantwortet nur "was ist JETZT dran".
  const aktuelleZiele = useMemo(
    () => meilensteine
      .filter(m => !m.done && m.ab <= heute())
      .sort((a, b) => a.ab.localeCompare(b.ab)),
    [meilensteine],
  );

  const faellig = useMemo(
    () => meine
      .filter(c => c.wiedervorlage && c.wiedervorlage <= heute())
      .sort((a, b) => (a.wiedervorlage ?? '').localeCompare(b.wiedervorlage ?? '')),
    [meine],
  );

  const offeneBerichte = useMemo(
    () => meine.filter(c => c.stufe === 'erstbericht-offen'),
    [meine],
  );

  const ohneFolgegeschaeft = useMemo(
    () => meine.filter(c => c.stufe === 'unterlagen' && !c.folgegeschaeft),
    [meine],
  );

  const bilanz = useMemo(() => {
    const ab = monatsBeginn();
    const zaehlen = (ziel: string) => meine.reduce((summe, c) =>
      summe + c.verlauf.filter(e => e.datum >= ab && zielStufe(e) === ziel).length, 0);
    return {
      ansprachen: zaehlen('angesprochen'),
      unterlagen: zaehlen('unterlagen'),
      berichte: zaehlen('geliefert'),
      gespraeche: zaehlen('erstgespraech'),
      auftraege: zaehlen('auftrag'),
    };
  }, [meine]);

  const kachel = (zahl: number, text: string, farbe: string) => (
    <div style={{
      flex: '1 1 130px', padding: '12px 14px', borderRadius: 8,
      background: 'var(--tn-surface)', border: '1px solid var(--tn-border)',
    }}>
      <div style={{ fontSize: 22, fontWeight: 800, color: farbe }}>{zahl}</div>
      <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 2 }}>{text}</div>
    </div>
  );

  const zeile = (c: SalesContact, rechts: string) => (
    <button
      key={c.id}
      data-ai-id={`sales-heute-kontakt-${c.id}`}
      onClick={() => onWechsel('kunden')}
      style={{
        display: 'flex', width: '100%', gap: 12, alignItems: 'baseline', textAlign: 'left',
        padding: '8px 12px', marginBottom: 4, borderRadius: 6, cursor: 'pointer',
        background: 'var(--tn-surface)', border: '1px solid var(--tn-border)', color: 'var(--tn-text)',
      }}
    >
      <span style={{ fontWeight: 600, fontSize: 13, flex: 1 }}>{c.firma}</span>
      {c.kontaktperson && (
        <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>{c.kontaktperson}</span>
      )}
      <span style={{ fontSize: 12, color: 'var(--tnf-e0af68)', whiteSpace: 'nowrap' }}>{rechts}</span>
    </button>
  );

  return (
    <div style={{ height: '100%', overflow: 'auto', padding: 20, background: 'var(--tn-bg)' }}>
      {loading && <div style={{ color: 'var(--tn-text-muted)', fontSize: 13 }}>Lädt…</div>}

      {error && (
        <div style={{
          padding: 12, borderRadius: 6, marginBottom: 16, fontSize: 13,
          background: 'rgba(247,118,142,0.12)', border: '1px solid var(--tnf-f7768e)', color: 'var(--tnf-f7768e)',
        }}>
          Der Trichter konnte nicht geladen werden: {error}
        </div>
      )}

      {!loading && (
        <div style={{ maxWidth: 860 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 4 }}>
            Guten Tag, {user?.name?.split(' ')[0] ?? ''}
          </div>
          <div style={{ fontSize: 13, color: 'var(--tn-text-muted)', marginBottom: 20 }}>
            {faellig.length === 0 && offeneBerichte.length === 0 && ungelesen === 0
              ? 'Heute steht nichts an. Ein guter Moment, einen Kontakt aus dem Trichter anzusprechen.'
              : 'Das ist heute dran.'}
          </div>

          {ungelesen > 0 && (
            <button
              data-ai-id="sales-heute-nachrichten"
              onClick={() => onWechsel('nachrichten')}
              style={{
                display: 'block', width: '100%', textAlign: 'left', marginBottom: 20,
                padding: '12px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                background: 'rgba(122,162,247,0.12)', border: '1px solid var(--tn-blue)', color: 'var(--tn-text)',
              }}
            >
              <strong>{ungelesen}</strong> ungelesene Nachricht{ungelesen === 1 ? '' : 'en'} von Rafael — ansehen →
            </button>
          )}

          {faellig.length > 0 && (
            <section style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Wiedervorlage fällig ({faellig.length})
              </div>
              {faellig.map(c => zeile(c, `seit ${formatDate(c.wiedervorlage)}`))}
            </section>
          )}

          {offeneBerichte.length > 0 && (
            <section style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Erstberichte offen ({offeneBerichte.length})
              </div>
              {offeneBerichte.map(c => zeile(c, 'wartet auf Lieferung'))}
              <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                Solange hier etwas steht: keine neue Ansprache. Die Wartezeit frisst den Eindruck —
                so steht es in der Strategie.
              </div>
            </section>
          )}

          {ohneFolgegeschaeft.length > 0 && (
            <section style={{ marginBottom: 22 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tnf-e0af68)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
                Unterlagen da, Folgegeschäft fehlt ({ohneFolgegeschaeft.length})
              </div>
              {ohneFolgegeschaeft.map(c => zeile(c, 'Folgegeschäft eintragen'))}
              <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 6, lineHeight: 1.5 }}>
                Bevor ein Erstbericht startet, muss stehen, was der Kunde danach will.
              </div>
            </section>
          )}

          <section style={{ marginBottom: 26 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Jetzt dran
              </div>
              <button
                data-ai-id="sales-heute-zu-zielen"
                onClick={() => onWechsel('ziele')}
                style={{ marginLeft: 'auto', fontSize: 12, background: 'none', border: 'none', color: 'var(--tn-blue)', cursor: 'pointer', padding: 0 }}
              >
                alle Ziele →
              </button>
            </div>
            {msFehler && (
              <div style={{ fontSize: 12, color: 'var(--tnf-f7768e)', marginBottom: 8 }}>Ziele: {msFehler}</div>
            )}
            {aktuelleZiele.length === 0 && !msFehler && (
              <div style={{ fontSize: 13, color: 'var(--tn-text-muted)' }}>
                Nichts offen, was schon angelaufen wäre.
              </div>
            )}
            {aktuelleZiele.map(m => {
              const offen = m.schritte.filter(s => !s.done);
              const meiner = m.wer === 'markus' || m.wer === userId;
              return (
                <div
                  key={m.id}
                  data-ai-id={`sales-heute-ziel-${m.id}`}
                  style={{
                    padding: '10px 12px', marginBottom: 5, borderRadius: 6, background: 'var(--tn-surface)',
                    border: `1px solid ${meiner ? 'var(--tn-blue)' : 'var(--tn-border)'}`,
                  }}
                >
                  <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--tn-text)' }}>{m.titel}</span>
                    <span style={{ fontSize: 12, color: 'var(--tn-text-muted)' }}>{m.wann}</span>
                    <span style={{
                      fontSize: 11, padding: '1px 7px', borderRadius: 9, background: 'var(--tn-bg)',
                      color: meiner ? 'var(--tn-blue)' : 'var(--tn-text-muted)', border: '1px solid var(--tn-border)',
                    }}>
                      {m.wer === 'beide' ? 'gemeinsam' : m.wer === 'rafael' ? 'Rafael' : 'du'}
                    </span>
                    {m.schritte.length > 0 && (
                      <span style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginLeft: 'auto' }}>
                        {m.schritte.length - offen.length}/{m.schritte.length}
                      </span>
                    )}
                  </div>
                  {offen.slice(0, 3).map(s => (
                    <label
                      key={s.id}
                      style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '3px 0', marginTop: 2, fontSize: 12.5, color: 'var(--tn-text)', cursor: 'pointer' }}
                    >
                      <input
                        type="checkbox"
                        data-ai-id={`sales-heute-schritt-${s.id}`}
                        checked={false}
                        onChange={() => hakenSchritt(m.id, s.id)}
                        style={{ marginTop: 2, cursor: 'pointer' }}
                      />
                      <span>{s.text}</span>
                    </label>
                  ))}
                  {offen.length > 3 && (
                    <div style={{ fontSize: 11, color: 'var(--tn-text-muted)', marginTop: 4 }}>
                      und {offen.length - 3} weitere
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          <section>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--tn-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 8 }}>
              Deine Bilanz — {monatsName()}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {kachel(bilanz.ansprachen, 'Ansprachen', 'var(--tn-text)')}
              {kachel(bilanz.unterlagen, 'Unterlagen erhalten', 'var(--tnf-7dcfff)')}
              {kachel(bilanz.berichte, 'Berichte geliefert', 'var(--tnf-bb9af7)')}
              {kachel(bilanz.gespraeche, 'Erstgespräche', 'var(--tnf-73daca)')}
              {kachel(bilanz.auftraege, 'Aufträge', 'var(--tnf-9ece6a)')}
            </div>
            <div style={{ fontSize: 12, color: 'var(--tn-text-muted)', marginTop: 10, lineHeight: 1.6 }}>
              Gezählt werden die Stufenwechsel deiner Kunden in diesem Monat — nicht Absichten.
              Der Zielrahmen aus der Strategie: acht Erstansprachen im Oktober, je zwölf im November
              und Dezember.
            </div>
          </section>
        </div>
      )}
    </div>
  );
}
